import {parseOptions, useLazyGetAssetsRelatedOptionsForInfiniteScrollingQuery} from '../../../../redux/api/assets';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {OptionLabelValue} from '../../../../types/internal/OptionLabelValue';
import {capitalize} from 'lodash';
import {useTranslation} from 'react-i18next';
import {
  AssetsFiltersFE,
  AssetsOptionLabelValueFilters,
  fromFeToBeAssetsFilters,
  StaticAssetsFilters
} from '../../../../types/internal/tables/assetsTable';
import NEWListWithSearchFilter from '../../../common/filters/NEWListWithSearchFilter';
import {useGetHazardTypeClassesQuery} from '../../../../redux/api/hazardTypes';
import {useParams} from 'react-router-dom';

interface HazardFilterCreationAreasProps {
  column: string,
  filters: AssetsFiltersFE,
  filtersInExcludeMode: AssetsOptionLabelValueFilters[],
  addFilters: (addFilters: AssetsFiltersFE) => void,
  removeFilters: (removeFilters: ((StaticAssetsFilters | 'hazards') | string)[] | ((StaticAssetsFilters | 'hazards') | string)) => void,
}

export default function HazardFilterCreationAreas({
  column,
  filters,
  filtersInExcludeMode,
  addFilters,
  removeFilters,
}: HazardFilterCreationAreasProps) {

  const {
    data: hazardTypes,
  } = useGetHazardTypeClassesQuery();
  const [getData, {
    data,
    isLoading,
    isFetching,
    isUninitialized,
    originalArgs,
  }] = useLazyGetAssetsRelatedOptionsForInfiniteScrollingQuery();
  const [querySearch, setQuerySearch] = useState<string>('');
  const options: OptionLabelValue[] = useMemo(() => parseOptions(data?.results || []), [data?.results]);
  const total = data?.count || 0;
  const {t} = useTranslation();
  const {uuid} = useParams();
  const placeholder = capitalize(t('table.filterPlaceholder', {column: hazardTypes?.find(ht => ht.code === column)?.name ?? ''}));

  const assetsTableFiltersWithoutCurrentHazardRef = useRef<AssetsFiltersFE>({});
  const selectedFilters: OptionLabelValue[] = filters?.hazards?.[column] || [];

  useEffect(() => {
    const {hazards, ...otherFilters} = filters;
    const {[column]: _currentHazard, ...otherHazards} = hazards || {};
    assetsTableFiltersWithoutCurrentHazardRef.current = {
      ...otherFilters,
      ...(Object.keys(otherHazards).length > 0 && {hazards: otherHazards}),
    };
  }, [filters, column]);

  const handleGetNext = useCallback(() => {
    getData({
      column,
      ...!!querySearch && {column_search: querySearch},
      ...fromFeToBeAssetsFilters(assetsTableFiltersWithoutCurrentHazardRef.current, filtersInExcludeMode),
      page: (originalArgs?.page || 0) + 1,
      page_size: 15,
      event_uuid: uuid,
    });
  }, [column, filtersInExcludeMode, getData, originalArgs?.page, querySearch, uuid]);

  useEffect(() => {
    getData({
      column,
      ...!!querySearch && {column_search: querySearch},
      ...fromFeToBeAssetsFilters(assetsTableFiltersWithoutCurrentHazardRef.current, filtersInExcludeMode),
      page: 1,
      page_size: 15,
      event_uuid: uuid,
    });
  }, [column, filtersInExcludeMode, getData, querySearch, uuid]);

  return <>
    <NEWListWithSearchFilter
      getNext={handleGetNext}
      options={options}
      selectedOptions={selectedFilters}
      setSelectedOptions={values => {
        if (values.length) {
          addFilters({hazards: {[column]: values}});
        } else {
          removeFilters(column);
        }
      }}
      total={total}
      isLoadingOptions={isLoading || isFetching || isUninitialized}
      querySearch={querySearch}
      setQuerySearch={setQuerySearch}
      search={{placeholder}}
    />
  </>;
}
